import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL, ERROR } from '../utils/constants';
import axios from 'axios';
import { setIsLoading, setShowMessage } from './shared.slice';
import { GridRowId } from '@mui/x-data-grid';
import b64toBlob from 'b64-to-blob';
import { EncounterStatusEnum } from '../Enum/EncounterStatusEnum';
import ClaimHistory from '../Models/Claims/ClaimHistory.model';
import { CHStatusEnum } from '../Enum/CHStatusEnum';
import { ExportModel } from '../utils/exportUtils';

const claimsDateFormatter = (date) => {
    return (
        (date.getMonth() > 8 ? date.getMonth() + 1 : '0' + (date.getMonth() + 1)) +
        '/' +
        (date.getDate() > 9 ? date.getDate() : '0' + date.getDate()) +
        '/' +
        date.getFullYear()
    );
};

export const statusValues = [
    { value: 0, label: 'All', type: 'Encounter', key: '0:Encounter' },
    { value: 1, label: 'For Review', type: 'Encounter', key: '1:Encounter' },
    { value: 2, label: 'To Submit', type: 'Encounter', key: '2:Encounter' },
    { value: 13, label: 'All', type: 'Claim', key: '13:Claim' },
    { value: 1, label: 'Submitted', type: 'Claim', key: '6:Claim' },
    { value: 4, label: 'Accepted By Waystar', type: 'Claim', key: '4:Claim' },
    { value: 9, label: 'Accepted by Intermediary', type: 'Claim', key: '9:Claim' },
    { value: 11, label: 'Denied by Payer', type: 'Claim', key: '11:Claim' },
    { value: 5, label: 'Rejected by Waystar', type: 'Claim', key: '5:Claim' },
    { value: 7, label: 'Rejected by Payer', type: 'Claim', key: '7:Claim' },
    { value: 8, label: 'Received by Payer', type: 'Claim', key: '8:Claim' },
    { value: 10, label: 'Paid by Payer', type: 'Claim', key: '10:Claim' },
    { value: 12, label: 'Closed by CHP', type: 'Claim', key: '12:Claim' },
];

const intialClaimHistory: ClaimHistory[] = [];

const claimsManagement = createSlice({
    name: 'claimsManagement',
    initialState: {
        claimsData: [],
        claimHistories: intialClaimHistory,
        claimNumberSearchValue: '',
        claimManagementSelectedStatus: '1:Encounter',
        dosStart: new Date(),
        dosEnd: new Date(),
        encounterIdList: [],
        selectedTab: 0,
        showClaimHistoryModal: false,
    },
    reducers: {
        _setClaimsData(state, action) {
            state.claimsData = action.payload;
        },
        _setClaimHistories(state, action) {
            state.claimHistories = action.payload;
        },
        _setClaimNumberSearchValue(state, action) {
            state.claimNumberSearchValue = action.payload;
        },
        _setClaimManagementSelectedStatus(state, action) {
            state.claimManagementSelectedStatus = action.payload;
        },
        _setDosStart(state, action) {
            state.dosStart = action.payload;
        },
        _setDosEnd(state, action) {
            state.dosEnd = action.payload;
        },
        _setEncounterIdList(state, action) {
            state.encounterIdList = action.payload;
        },
        _setSelectedTab(state, action) {
            state.selectedTab = action.payload;
        },
        _setShowClaimHistoryModal(state, action) {
            state.showClaimHistoryModal = action.payload;
        },
    },
});

const {
    _setClaimsData,
    _setClaimHistories,
    _setClaimNumberSearchValue,
    _setClaimManagementSelectedStatus,
    _setDosStart,
    _setDosEnd,
    _setEncounterIdList,
    _setSelectedTab,
    _setShowClaimHistoryModal,
} = claimsManagement.actions;

export const getStatuses = (onlyClaims: boolean = false) => {
    return onlyClaims ?
        [
            {
                Name: 'Claims',
                List: statusValues.filter((s) => s.type === 'Claim'),
            },
        ]
        :
        [
            {
                Name: 'Member Encounters',
                List: statusValues.filter((s) => s.type === 'Encounter'),
            },
            {
                Name: 'Claims',
                List: statusValues.filter((s) => s.type === 'Claim'),
            },
        ];
};

export const exportClaimsData =
    (statusKeyValue: string, dosStart: Date, dosEnd: Date, exportModels: ExportModel[], claimNumber: string = null, healthPlanId: string = 'All'): AppThunk =>
        async (dispatch) => {
            dispatch(setIsLoading(true));
            try {
                const selectedClaimManagementStatus = statusValues.find((x) => x.key === statusKeyValue);
                var getURL = `${API_URL}/encounter/exportclaims?encounterStatusValue=${selectedClaimManagementStatus.value}&startDate=${claimsDateFormatter(
                    dosStart
                )}&endDate=${claimsDateFormatter(dosEnd)}&claimNumber=${claimNumber ?? ''}&healthPlanId=${healthPlanId}`;

                if (selectedClaimManagementStatus.type === 'Encounter') {
                    getURL = `${API_URL}/encounter/exportencounters?encounterStatusValue=${selectedClaimManagementStatus.value}&startDate=${claimsDateFormatter(
                        dosStart
                    )}&endDate=${claimsDateFormatter(dosEnd)}&claimNumber=${claimNumber}`;
                }

                const response = await axios.post(getURL, { exportModels: exportModels });
                let blob = b64toBlob(response.data.base64String, 'text/csv');
                let url = window.URL.createObjectURL(blob);
                let link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${Date.now()}.xlsx`);
                link.click();
                link.remove();
            } catch (e) {
                dispatch(setShowMessage(true, e.message, ERROR));
            }
            dispatch(setIsLoading(false));
        };

export const fetchClaimsData =
    (statusKeyValue: string, dosStart: Date, dosEnd: Date, healthPlanId: string = 'All'): AppThunk =>
        async (dispatch) => {
            dispatch(setIsLoading(true));
            try {
                const selectedClaimManagementStatus = statusValues.find((x) => x.key === statusKeyValue);
                var getURL = `${API_URL}/encounter/getclaims?encounterStatusValue=${selectedClaimManagementStatus.value}&startDate=${claimsDateFormatter(
                    dosStart
                )}&endDate=${claimsDateFormatter(dosEnd)}&healthPlanId=${healthPlanId}`;

                if (selectedClaimManagementStatus.type === 'Encounter') {
                    getURL = `${API_URL}/encounter/getencounters?encounterStatusValue=${selectedClaimManagementStatus.value}&startDate=${claimsDateFormatter(
                        dosStart
                    )}&endDate=${claimsDateFormatter(dosEnd)}`;
                }

                const response = await axios.get(getURL);
                dispatch(_setClaimsData(response.data));
            } catch (e) {
                dispatch(setShowMessage(true, e.message, ERROR));
            }
            dispatch(setIsLoading(false));
        };

export const fetchClaimsByClaimNumber =
    (claimNumber: string, functionCall: Function): AppThunk =>
        async (dispatch, getState) => {
            dispatch(setIsLoading(true));
            try {
                const response = await axios.get(`${API_URL}/encounter/getclaimbyclaimnumber?claimNumber=${claimNumber}`);
                dispatch(_setClaimsData(response.data));

                if (response.data[0]) {
                    dispatch(_setClaimManagementSelectedStatus(statusValues.find((x) => x.value === response.data[0].CHStatus).key));
                    functionCall(response.data[0].Status);
                }
            } catch (e) {
                dispatch(setShowMessage(true, e.message, ERROR));
            }
            dispatch(setIsLoading(false));
        };

export const fetchClaimHistory =
    (claimNumber: string): AppThunk =>
        async (dispatch) => {
            dispatch(setIsLoading(true));
            const response = await axios.get(`${API_URL}/encounter/getclaimhistory?claimNumber=${claimNumber}`);
            dispatch(_setClaimHistories(response.data));
            dispatch(setIsLoading(false));
        };

export const fetchClaimResponses =
    (callback: Function): AppThunk =>
        async (dispatch) => {
            dispatch(setIsLoading(true));
            await axios.post(`${API_URL}/encounter/downloadclaims`);
            callback();
        };

export const processClaimResponses =
    (callback: Function): AppThunk =>
        async (dispatch) => {
            dispatch(setIsLoading(true));
            await axios.post(`${API_URL}/encounter/processclaims`);
            callback();
        };

export const setClaimManagementSelectedStatus =
    (value: string): AppThunk =>
        (dispatch) => {
            dispatch(_setClaimManagementSelectedStatus(value));
        };

export const setClaimNumberSearchValue =
    (value: string): AppThunk =>
        (dispatch) => {
            dispatch(_setClaimNumberSearchValue(value));
        };

export const setDosStart =
    (value: Date): AppThunk =>
        (dispatch) => {
            dispatch(_setDosStart(value));
        };

export const setDosEnd =
    (value: Date): AppThunk =>
        (dispatch) => {
            dispatch(_setDosEnd(value));
        };

export const setEncounterIdList =
    (value: GridRowId[]): AppThunk =>
        (dispatch) => {
            dispatch(_setEncounterIdList(value));
        };

export const generateClaims =
    (encounterIdList: GridRowId[], statusKeyValue: string, dosStart: Date, dosEnd: Date): AppThunk =>
        async (dispatch) => {
            dispatch(setIsLoading(true));
            try {
                const response = await axios.post(`${API_URL}/encounter/generateclaims`, encounterIdList);
                console.log(`generateclaimsresponse: `, response);
                dispatch(setIsLoading(false));
                dispatch(fetchClaimsData(statusKeyValue, dosStart, dosEnd));
            } catch (e) {
                dispatch(setShowMessage(true, e.message, ERROR));
                dispatch(setIsLoading(false));
            }
        };

export const downloadClaimsHandler =
    (claimsData): AppThunk =>
        async (dispatch) => {
            const downloadList = claimsData.map((item) => item.EncounterId);
            const response = await axios.post(`${API_URL}/encounter/claimsManagementDownload`, downloadList);
            let blob = b64toBlob(response.data.base64String, 'text/csv');
            let url = window.URL.createObjectURL(blob);
            let link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${Date.now()}.xlsx`);
            link.click();
            link.remove();
        };

export const parseMolinaPdfToExcel =
    (file: File): AppThunk =>
        async (dispatch) => {
            dispatch(setIsLoading(true));
            try {
                const formData = new FormData();
                formData.append('file', file);

                const response = await axios.post(`${API_URL}/encounter/parsemolinapdftoexcel`, formData, {
                    responseType: 'blob',
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                const disposition = response.headers?.['content-disposition'] as string | undefined;
                const fileNameMatch = disposition ? disposition.match(/filename=\"?([^\";]+)\"?/) : null;
                const uploadedBaseName = file.name.replace(/\.pdf$/i, '');
                const fallbackFileName = `${uploadedBaseName}.xlsx`;
                const fileName = fileNameMatch && fileNameMatch[1] ? fileNameMatch[1] : fallbackFileName;
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (e) {
                dispatch(setShowMessage(true, e.message, ERROR));
            }
            dispatch(setIsLoading(false));
        };

export const setSelectedTab =
    (tab: number): AppThunk =>
        async (dispatch) => {
            dispatch(_setSelectedTab(tab));
        };

export const setShowClaimHistoryModal =
    (value: boolean): AppThunk =>
        async (dispatch) => {
            dispatch(_setShowClaimHistoryModal(value));
        };

export const getClaimStatus = (statusValue: number) => {
    if (statusValue === CHStatusEnum.Open) {
        return 'Submitted';
    } else if (statusValue === CHStatusEnum.AcceptedWaystar) {
        return 'Accepted by Waystar';
    } else if (statusValue === CHStatusEnum.RejectedWaystar) {
        return 'Rejected by Waystar';
    } else if (statusValue === CHStatusEnum.AcceptedIntermediary) {
        return 'Accepted by Intermediary';
    } else if (statusValue === CHStatusEnum.RejectedPayer) {
        return 'Rejected by Payer';
    } else if (statusValue === CHStatusEnum.ReceivedPayer) {
        return 'Received by Payer';
    } else if (statusValue === CHStatusEnum.PaidByPayer) {
        return 'Paid by Payer';
    } else if (statusValue === CHStatusEnum.DeniedByPayer) {
        return 'Denied by Payer';
    } else if (statusValue === CHStatusEnum.ClosedByCHP) {
        return 'Closed by CHP';
    }

    if (statusValue === EncounterStatusEnum.ForReview) {
        return 'For Review';
    } else if (statusValue === EncounterStatusEnum.ToSubmit) {
        return 'To Submit';
    }
};

export default claimsManagement.reducer;
